/**
 * #547 / #250 read model: what AI a Worker or the Overseer is configured to run,
 * what its computer reports it is actually running, and what Brunel recommends -
 * three separate answers, never merged.
 *
 * Canonical sources only:
 * - brunel.worker_ai_execution_profiles: Auto/Pinned, configured offering/runtime/
 *   model/effort, node-reported actual runtime/model/effort, busy;
 * - brunel.ai_runtime_capabilities: which efforts each runtime on each computer
 *   can actually apply (none = model default);
 * - node_offerings / model_offerings / runtime_proof for choices and proof.
 *
 * Unknown stays Unknown: an actual identity the computer has not reported is not
 * inferred from the configuration, and effort is never guessed from a launch
 * catalog. Model choices come only from offerings on that computer; an exact pin
 * that is no longer offered stays visible as unavailable, never swapped.
 */
import { assessAssignedActual, compareConfiguredActual, splitModelIdentity, type ExecutionComparison } from '@brunel/shared';
import type {
  AiRuntimeCapabilityRow, CommandPlannerHostStatus, ModelOfferingRef, NodeOffering, NodePresence, RuntimeProof,
  WorkerAiExecutionProfileRow, WorkerTerminalRow, WorkforceSession,
} from './data';
import type { MainCommandReplyIdentity } from './main-command-recipients';
import { AI_CONNECTIONS_DISPLAY } from './ai-connections-catalog';

export type AiProfileMode = 'Auto' | 'Pinned' | 'Task override' | 'Unknown';
export type AiProfileApplyMode = 'now' | 'after_current_task';

export interface AiIdentityLine {
  readonly provider: string;
  readonly runtime: string;
  readonly model: string;
  readonly effort: string;
}

export interface AiProfileModelOption {
  readonly offeringKey: string;
  readonly runtimeKey: string | null;
  readonly label: string;
  readonly available: boolean;
  readonly reason: string | null;
}

export interface AiProfileEffortChoices {
  readonly efforts: readonly string[];
  /** Why no effort can be chosen for this runtime; null when choices exist. */
  readonly reason: string | null;
  /** Whether a live session takes the change in place (keeps its terminal). */
  readonly liveSwitch: boolean;
}

export interface AiExecutionProfileView {
  readonly targetKind: 'overseer' | 'worker';
  readonly targetId: string;
  readonly title: string;
  readonly nodeId: string | null;
  readonly node: string;
  readonly source: string;
  readonly mode: AiProfileMode;
  readonly availability: string;
  readonly proof: string;
  /** What Brunel is set to use. Null: nothing configured. */
  readonly configured: AiIdentityLine | null;
  readonly configuredLabel: string;
  /** What the computer reported it is actually running. Null: not reported (Unknown). */
  readonly actual: AiIdentityLine | null;
  readonly actualLabel: string;
  readonly actualReportedAt: string | null;
  /**
   * not_proven: the runtime matches but no execution evidence names the resolved model yet.
   * A Worker's mismatch is only a different runtime, a resolved model outside what the
   * configured offering allows, or a pinned effort that was not applied.
   */
  readonly comparison: ExecutionComparison['status'] | 'not_proven';
  readonly mismatch: boolean;
  /** The runtime alias the actual model key names (model left to the runtime); null when none. */
  readonly actualRuntimeAlias: string | null;
  /** The concrete model execution evidence names; null when not yet proven. */
  readonly actualResolvedModel: string | null;
  /** True only when Brunel knows this Worker has never reported a run or a live AI. */
  readonly neverRun: boolean;
  readonly recommendedLabel: string | null;
  readonly recommendationReason: string | null;
  /** The recommended offering key, from run proof on this computer; null when none is justified. */
  readonly recommendedOfferingKey: string | null;
  /** Whether the current selection is the recommended offering (shown once, not twice). */
  readonly selectedIsRecommended: boolean;
  readonly busy: boolean;
  readonly defaultApplyMode: AiProfileApplyMode;
  readonly configuredOfferingKey: string | null;
  readonly configuredEffortKey: string | null;
  readonly modelOptions: readonly AiProfileModelOption[];
  readonly effortsByRuntime: Readonly<Record<string, AiProfileEffortChoices>>;
  /** Whether the owner can change this target through set_ai_execution_profile. */
  readonly controllable: boolean;
  readonly controlReason: string | null;
  readonly identityUnavailable: boolean;
}

const UNKNOWN = 'Unknown';
const NOT_REPORTED = 'Unknown — not reported by the computer yet';
/**
 * #661: a Worker's Actual is only what is live now. None reported, or the AI exited, retired,
 * failed, or ran in an earlier node process: no live AI is known - never the configured one.
 */
const NO_LIVE_AI = 'Unknown — no live AI reported by the computer';
/** A Worker with no run and no live AI on record: nothing is proven either way, never Unknown. */
export const NOT_YET_PROVEN = 'Not yet proven — no run or live AI on record';

function titleCase(value: string): string {
  return value.split(/[_-]+/).filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ');
}

/** A model key equal to its runtime key is how "the CLI's own default model" is registered. */
export function modelLabel(runtimeKey: string | null, modelKey: string | null): string {
  if (modelKey === null) return UNKNOWN;
  if (runtimeKey !== null && modelKey === runtimeKey) return 'CLI default model';
  return modelKey;
}

export function effortLabel(effortKey: string | null, fallback: string): string {
  return effortKey === null ? fallback : titleCase(effortKey);
}

function lineLabel(line: AiIdentityLine | null, empty: string): string {
  if (line === null) return empty;
  return `${line.runtime} · ${line.model} · ${line.effort}`;
}

function sourceFor(offering: ModelOfferingRef | null): string {
  if (offering === null) return UNKNOWN;
  const displays = AI_CONNECTIONS_DISPLAY.filter((row) => row.runtimeKeys.includes(offering.runtime_key));
  if (displays.some((row) => row.meterMode === 'local_unlimited')) return 'Local';
  if (displays.length > 0) return 'Subscription';
  return offering.execution_location === 'local' ? 'Local' : 'Cloud';
}

function proofFor(runtimeKey: string | null, nodeId: string | null, proofs: readonly RuntimeProof[]): RuntimeProof | null {
  if (runtimeKey === null || nodeId === null) return null;
  return proofs.find((proof) => proof.node_id === nodeId && proof.runtime_key === runtimeKey) ?? null;
}

function proofLabel(proof: RuntimeProof | null): string {
  if (proof === null || proof.run_count === 0) return 'Unproven';
  if (proof.proven) return 'Proven';
  return proof.failure_count > 0 ? 'Failing' : 'Unproven';
}

function modelOptions(
  nodeId: string | null,
  configuredKey: string | null,
  nodeOfferings: readonly NodeOffering[],
  offerings: readonly ModelOfferingRef[],
): readonly AiProfileModelOption[] {
  if (nodeId === null) return [];
  const availableKeys = new Set(nodeOfferings.filter((row) => row.node_id === nodeId).map((row) => row.offering_key));
  const keys = [...availableKeys].sort();
  // An exact pin that is no longer offered stays visible, never silently replaced.
  if (configuredKey !== null && !availableKeys.has(configuredKey)) keys.unshift(configuredKey);
  return keys.map((key) => {
    const offering = offerings.find((row) => row.key === key) ?? null;
    const available = availableKeys.has(key);
    return {
      offeringKey: key,
      runtimeKey: offering?.runtime_key ?? null,
      label: offering === null
        ? key
        : `${offering.provider_label} · ${offering.runtime_key} · ${modelLabel(offering.runtime_key, offering.model_key)}`,
      available,
      reason: available ? null : 'Not offered on this computer; Brunel will not substitute another model',
    };
  });
}

function effortsFor(nodeId: string | null, capabilities: readonly AiRuntimeCapabilityRow[]): Readonly<Record<string, AiProfileEffortChoices>> {
  const out: Record<string, AiProfileEffortChoices> = {};
  if (nodeId === null) return out;
  for (const row of capabilities.filter((entry) => entry.node_id === nodeId)) {
    out[row.runtime_key] = {
      efforts: row.efforts,
      reason: row.efforts.length === 0 ? 'Effort is the model default; this runtime has no effort control.' : null,
      liveSwitch: row.live_model_switch && row.live_effort_switch,
    };
  }
  return out;
}

/** Effort choices for one runtime on this target's computer, or exactly why there are none. */
export function effortChoicesFor(view: AiExecutionProfileView, runtimeKey: string | null): AiProfileEffortChoices {
  if (runtimeKey === null) return { efforts: [], reason: 'Choose a model first.', liveSwitch: false };
  return view.effortsByRuntime[runtimeKey]
    ?? { efforts: [], reason: 'This computer has not reported which efforts this runtime supports.', liveSwitch: false };
}

/**
 * #250 Recommended: the run-record part of brunel_engine.best_offering, read from runtime_proof
 * on this computer - more successes than failures first, then fewer failures. Only offerings on
 * this computer whose runtime has recorded successful proof there qualify. When the record cannot
 * separate the leaders (best_offering would fall to price and memory, which the browser does not
 * read), no single recommendation is claimed.
 */
function recommendation(
  nodeId: string | null,
  options: readonly AiProfileModelOption[],
  offerings: readonly ModelOfferingRef[],
  proofs: readonly RuntimeProof[],
): { key: string | null; label: string | null; reason: string | null } {
  const none = { key: null, label: null, reason: 'No proven recommendation yet' };
  if (nodeId === null) return none;
  const available = options.filter((option) => option.available);
  const proven = available.flatMap((option) => {
    const offering = offerings.find((row) => row.key === option.offeringKey) ?? null;
    const proof = proofFor(offering?.runtime_key ?? null, nodeId, proofs);
    return offering !== null && proof?.proven === true ? [{ offering, proof }] : [];
  }).sort((a, b) => (b.proof.success_count - b.proof.failure_count) - (a.proof.success_count - a.proof.failure_count)
    || a.proof.failure_count - b.proof.failure_count);
  const best = proven[0];
  if (best === undefined) return none;
  const runnerUp = proven[1];
  if (runnerUp !== undefined
    && runnerUp.proof.success_count - runnerUp.proof.failure_count === best.proof.success_count - best.proof.failure_count
    && runnerUp.proof.failure_count === best.proof.failure_count) {
    return { key: null, label: null, reason: 'No single recommendation: proven offerings tie on this computer\'s run record' };
  }
  const record = `${best.proof.success_count} success${best.proof.success_count === 1 ? '' : 'es'}`
    + `, ${best.proof.failure_count} failure${best.proof.failure_count === 1 ? '' : 's'} here`;
  return {
    key: best.offering.key,
    label: `${best.offering.runtime_key} · ${modelLabel(best.offering.runtime_key, best.offering.model_key)}`,
    reason: available.length === 1 ? `Only available offering, proven · ${record}`
      : proven.length === 1 ? `Only proven offering on this computer · ${record}`
        : `Best run record on this computer · ${record}`,
  };
}

export const OVERSEER_NOT_CONTROLLABLE =
  'Configure an available Planner Host computer and runtime before choosing the Overseer model and effort.';

/**
 * The Overseer uses the existing Planner Host and management-role route. Configured
 * comes from that exact route; Actual comes only from a node-submitted planner claim.
 */
export function overseerAiProfile(input: MainCommandReplyIdentity | {
  readonly identity: MainCommandReplyIdentity;
  readonly status: CommandPlannerHostStatus | null;
  readonly nodeOfferings: readonly NodeOffering[];
  readonly offerings: readonly ModelOfferingRef[];
  readonly proofs: readonly RuntimeProof[];
  readonly capabilities: readonly AiRuntimeCapabilityRow[];
}): AiExecutionProfileView {
  const detailed = 'identity' in input;
  const identity = detailed ? input.identity : input;
  const status = detailed ? input.status : null;
  const offerings = detailed ? input.offerings : [];
  const nodeOfferings = detailed ? input.nodeOfferings : [];
  const proofs = detailed ? input.proofs : [];
  const capabilities = detailed ? input.capabilities : [];
  const nodeId = status?.node_id ?? identity.nodeId;
  const configuredKey = status?.configured_offering_key ?? identity.offeringKey;
  const configuredOffering = offerings.find((offering) => offering.key === configuredKey) ?? null;
  const configuredRuntime = configuredOffering?.runtime_key ?? (configuredKey === null ? null : identity.runtime);
  const configuredModel = status?.configured_model_key ?? configuredOffering?.model_key
    ?? (configuredKey === null ? null : identity.model);
  const configuredEffort = status?.configured_effort_key ?? null;
  const identityUnavailable = configuredKey === null
    || (identity.provider === UNKNOWN && identity.runtime === UNKNOWN && identity.model === UNKNOWN);
  const configured: AiIdentityLine | null = identityUnavailable ? null : {
    provider: configuredOffering?.provider_label ?? identity.provider,
    runtime: configuredRuntime ?? UNKNOWN,
    model: modelLabel(configuredRuntime, configuredModel),
    effort: status?.configured_at != null ? effortLabel(configuredEffort, 'Model default') : identity.thinking,
  };
  const actualReported = status?.actual_reported_at != null && status.actual_runtime_key !== null;
  const actualOffering = !actualReported ? null : offerings.find((offering) =>
    offering.runtime_key === status!.actual_runtime_key && offering.model_key === status!.actual_model_key) ?? null;
  const actual: AiIdentityLine | null = !actualReported ? null : {
    provider: actualOffering?.provider_label ?? UNKNOWN,
    runtime: status!.actual_runtime_key!,
    model: modelLabel(status!.actual_runtime_key, status!.actual_model_key),
    effort: effortLabel(status!.actual_effort_key, 'Model default'),
  };
  const comparison = compareConfiguredActual({
    configured: { runtimeKey: configuredRuntime, modelKey: configuredModel, effortKey: configuredEffort },
    actual: actualReported ? {
      runtimeKey: status!.actual_runtime_key, modelKey: status!.actual_model_key, effortKey: status!.actual_effort_key,
    } : null,
    configuredAt: status?.configured_at ?? null,
    actualAt: status?.actual_reported_at ?? null,
  });
  const hostRuntime = status?.runtime_key ?? null;
  const allowed = new Set(offerings.filter((offering) => offering.runtime_key === hostRuntime).map((offering) => offering.key));
  const options = modelOptions(nodeId, configuredKey,
    nodeOfferings.filter((row) => allowed.has(row.offering_key)), offerings);
  const proof = proofFor(configuredRuntime, nodeId, proofs);
  const recommended = recommendation(nodeId, options, offerings, proofs);
  const controllable = detailed && status !== null && nodeId !== null;
  return {
    targetKind: 'overseer',
    targetId: 'overseer',
    title: 'Overseer',
    nodeId,
    node: identity.node,
    source: configuredOffering === null ? identity.sourceLabel : sourceFor(configuredOffering),
    mode: status?.configured_at != null ? 'Pinned' : 'Unknown',
    availability: identity.availabilityLabel,
    proof: proof === null ? (identity.available ? 'Available' : 'Unavailable') : proofLabel(proof),
    configured,
    configuredLabel: lineLabel(configured, 'Overseer identity unavailable / not configured'),
    actual,
    actualLabel: lineLabel(actual, NOT_REPORTED),
    actualReportedAt: status?.actual_reported_at ?? null,
    comparison: comparison.status,
    mismatch: comparison.status === 'mismatch',
    actualRuntimeAlias: null,
    actualResolvedModel: null,
    neverRun: false,
    recommendedLabel: recommended.label,
    recommendationReason: recommended.reason,
    recommendedOfferingKey: recommended.key,
    selectedIsRecommended: recommended.key !== null && recommended.key === configuredKey,
    busy: false,
    defaultApplyMode: 'now',
    configuredOfferingKey: configuredKey,
    configuredEffortKey: configuredEffort,
    modelOptions: options,
    effortsByRuntime: effortsFor(nodeId, capabilities),
    controllable,
    controlReason: controllable ? null : OVERSEER_NOT_CONTROLLABLE,
    identityUnavailable,
  };
}

export function workerAiProfile(input: {
  readonly workerId: string;
  readonly workerNumber: number;
  readonly workerName: string;
  readonly terminal: WorkerTerminalRow | null;
  readonly session: WorkforceSession | null;
  /** brunel.worker_ai_execution_profiles row for this Worker's current session, when it exists. */
  readonly executionProfile: WorkerAiExecutionProfileRow | null;
  readonly taskOverrideOfferingKey?: string | null;
  readonly nodes: readonly NodePresence[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly offerings: readonly ModelOfferingRef[];
  readonly proofs: readonly RuntimeProof[];
  readonly capabilities: readonly AiRuntimeCapabilityRow[];
  /** Fallback when no execution profile row is readable. */
  readonly busy: boolean;
  /**
   * This Worker's newest finished run (finished_runs), the execution evidence of the model that
   * answered. Null: Brunel read the runs and this Worker has none. Absent: not read (Unknown).
   */
  readonly latestRun?: { readonly runtimeKey: string | null; readonly modelKey: string | null; readonly reportedAt: string } | null;
}): AiExecutionProfileView {
  const row = input.executionProfile;
  const nodeId = row?.node_id ?? input.terminal?.node_id ?? input.session?.node_id ?? null;
  const taskOverride = input.taskOverrideOfferingKey?.trim() || null;
  const configuredKey = taskOverride ?? row?.configured_offering_key ?? input.session?.offering_key ?? null;
  const configuredOffering = input.offerings.find((offering) => offering.key === configuredKey) ?? null;
  const mode: AiProfileMode = taskOverride !== null ? 'Task override'
    : row === null ? 'Unknown' : row.mode === 'pinned' ? 'Pinned' : 'Auto';
  const configuredRuntime = row?.configured_runtime_key ?? configuredOffering?.runtime_key ?? null;
  const configuredModel = row?.configured_model_key ?? configuredOffering?.model_key ?? null;
  const configuredEffortKey = taskOverride === null ? row?.configured_effort_key ?? null : null;
  const configured: AiIdentityLine | null = configuredKey === null ? null : {
    provider: configuredOffering?.provider_label ?? UNKNOWN,
    runtime: configuredRuntime ?? UNKNOWN,
    model: modelLabel(configuredRuntime, configuredModel),
    effort: row === null ? UNKNOWN : effortLabel(configuredEffortKey, 'Model default'),
  };
  const actualReported = row?.actual_reported_at != null && row.actual_runtime_key !== null;
  // The live row records what this node applied (often the runtime alias); the model that
  // answered is the resolved one a run of that same live AI recorded after it was reported.
  const run = input.latestRun ?? null;
  const applied = splitModelIdentity(actualReported ? row!.actual_runtime_key : null, actualReported ? row!.actual_model_key : null);
  const runtimeAlias = applied.runtimeAlias;
  const resolvedModel = applied.resolvedModel ?? (actualReported && run !== null
    && run.runtimeKey === row!.actual_runtime_key && Date.parse(run.reportedAt) >= Date.parse(row!.actual_reported_at!)
    ? splitModelIdentity(run.runtimeKey, run.modelKey).resolvedModel : null);
  const assessment = assessAssignedActual({
    assigned: { runtimeKey: configuredRuntime, modelKey: configuredModel },
    actual: actualReported ? { runtimeKey: row!.actual_runtime_key, modelKey: resolvedModel } : null,
    offerings: input.offerings.map((offering) => ({ runtimeKey: offering.runtime_key, modelKey: offering.model_key })),
  });
  const actualOffering = actualReported
    ? input.offerings.find((offering) => offering.runtime_key === row!.actual_runtime_key && offering.model_key === row!.actual_model_key) ?? null
    : null;
  const actualModel = resolvedModel !== null
    ? (runtimeAlias === null ? resolvedModel : `${resolvedModel} (resolved from the ${runtimeAlias} alias)`)
    : runtimeAlias !== null ? `CLI default model (${runtimeAlias} alias) — resolved model not yet proven`
      : UNKNOWN;
  const actual: AiIdentityLine | null = !actualReported ? null : {
    provider: actualOffering?.provider_label ?? UNKNOWN,
    runtime: row!.actual_runtime_key!,
    model: actualModel,
    effort: effortLabel(row!.actual_effort_key, 'Model default'),
  };
  const timing = compareConfiguredActual({
    configured: { runtimeKey: configuredRuntime, modelKey: configuredModel, effortKey: configuredEffortKey },
    actual: actualReported ? { runtimeKey: row!.actual_runtime_key, modelKey: row!.actual_model_key, effortKey: row!.actual_effort_key } : null,
    configuredAt: row?.configured_at ?? null,
    actualAt: row?.actual_reported_at ?? null,
  });
  const comparison: AiExecutionProfileView['comparison'] = timing.status === 'unknown' || timing.status === 'pending' ? timing.status
    : assessment.status === 'differs' || timing.differences.includes('effort') ? 'mismatch'
      : assessment.status;
  const neverRun = !actualReported && input.latestRun === null;
  const options = modelOptions(nodeId, configuredKey, input.nodeOfferings, input.offerings);
  const offered = configuredKey !== null && options.some((option) => option.offeringKey === configuredKey && option.available);
  const node = nodeId === null ? null : input.nodes.find((entry) => entry.node_id === nodeId) ?? null;
  const proof = proofFor(configuredRuntime, nodeId, input.proofs);
  const recommended = recommendation(nodeId, options, input.offerings, input.proofs);
  const busy = row?.busy ?? input.busy;
  const controllable = row !== null && nodeId !== null;
  return {
    targetKind: 'worker',
    targetId: input.workerId,
    title: `Worker ${input.workerNumber} · ${input.workerName}`,
    nodeId,
    node: node?.name ?? input.terminal?.node_name ?? UNKNOWN,
    source: sourceFor(configuredOffering),
    mode,
    availability: node === null ? 'No active computer'
      : node.status === 'offline' ? 'Computer offline'
        : !offered ? 'Configured offering unavailable on this computer' : 'Available',
    proof: proofLabel(proof),
    configured,
    configuredLabel: lineLabel(configured, 'Not configured'),
    actual,
    actualLabel: lineLabel(actual, neverRun ? NOT_YET_PROVEN : NO_LIVE_AI),
    actualReportedAt: row?.actual_reported_at ?? null,
    comparison,
    mismatch: comparison === 'mismatch',
    actualRuntimeAlias: actualReported ? runtimeAlias : null,
    actualResolvedModel: actualReported ? resolvedModel : null,
    neverRun,
    recommendedLabel: recommended.label,
    recommendationReason: recommended.reason,
    recommendedOfferingKey: recommended.key,
    selectedIsRecommended: recommended.key !== null && recommended.key === configuredKey,
    busy,
    defaultApplyMode: busy ? 'after_current_task' : 'now',
    configuredOfferingKey: configuredKey,
    configuredEffortKey,
    modelOptions: options,
    effortsByRuntime: effortsFor(nodeId, input.capabilities),
    controllable,
    controlReason: controllable ? null : 'This Worker has no current session on a computer, so there is nothing to apply a profile to.',
    identityUnavailable: configured === null && actual === null,
  };
}

export interface WorkerCardAiStatus {
  /** What the computer reports this Worker is running now; never the configured choice. */
  readonly using: string;
  readonly usingKnown: boolean;
  readonly mode: AiProfileMode;
  readonly recommendation: string;
  readonly recommended: 'selected' | 'other' | 'none';
  /** Why the selection cannot run as chosen (for example, an unavailable pin); null when it can. */
  readonly blocked: string | null;
}

/** #250 the Worker card's compact AI answer, from the same read model as the expanded strip. */
export function workerCardAiStatus(view: AiExecutionProfileView): WorkerCardAiStatus {
  const reason = view.recommendationReason === null ? '' : ` · ${view.recommendationReason}`;
  return {
    using: view.actual === null ? (view.neverRun ? 'Not yet proven' : 'Unknown — not reported') : `${view.actual.runtime} · ${view.actual.model}`,
    usingKnown: view.actual !== null,
    mode: view.mode,
    recommendation: view.selectedIsRecommended ? `Recommended — the selected AI${reason}`
      : view.recommendedLabel !== null ? `Recommended: ${view.recommendedLabel}${reason}`
        : view.recommendationReason ?? 'No proven recommendation yet',
    recommended: view.selectedIsRecommended ? 'selected' : view.recommendedLabel !== null ? 'other' : 'none',
    blocked: view.configuredOfferingKey !== null && view.availability === 'Configured offering unavailable on this computer'
      ? 'Selected AI is not on this computer — dispatch blocked, no substitute'
      : null,
  };
}

/** Owner-facing sentence for each set_ai_execution_profile refusal. */
export function aiProfileErrorMessage(code: string): string {
  switch (code) {
    case 'unsupported_effort': return 'That effort is not supported by this runtime on this computer.';
    case 'effort_capability_unknown': return 'This computer has not reported which efforts this runtime supports yet.';
    case 'offering_not_on_node': return 'That model is not offered on this computer. Brunel will not substitute another model.';
    case 'worker_busy': return 'This Worker is busy. A model or effort never changes mid-turn: choose "Apply after current task", or stop the run first.';
    case 'planner_path_not_controllable': return OVERSEER_NOT_CONTROLLABLE;
    case 'planner_host_mismatch': return 'That choice does not match the Overseer\'s Planner Host computer and runtime.';
    case 'node_has_no_runtimes': return 'This computer has no runtime for Auto to choose.';
    case 'forbidden': return 'Confirm it is you (two-step verification) to change AI settings.';
    case 'not_found': return 'That Worker or computer no longer has a current session.';
    case 'invalid_input': return 'That combination is not valid (for example, an effort needs an exact model).';
    default: return 'The change was not applied.';
  }
}

/** The one structured request, exactly the keys set_ai_execution_profile accepts. */
export function aiProfileRequest(input: {
  readonly orgId: string;
  readonly view: AiExecutionProfileView;
  readonly offeringKey: string | null;
  readonly effortKey: string | null;
  readonly applyMode: AiProfileApplyMode;
}): {
  readonly orgId: string; readonly targetKind: 'worker' | 'overseer'; readonly targetId: string; readonly nodeId: string;
  readonly offeringKey: string | null; readonly effortKey: string | null; readonly applyMode: AiProfileApplyMode;
} | null {
  if (!input.view.controllable || input.view.nodeId === null) return null;
  return {
    orgId: input.orgId,
    targetKind: input.view.targetKind,
    targetId: input.view.targetKind === 'overseer' ? input.orgId : input.view.targetId,
    nodeId: input.view.nodeId,
    offeringKey: input.offeringKey,
    effortKey: input.offeringKey === null ? null : input.effortKey,
    applyMode: input.applyMode,
  };
}

export interface AiProfileApplyResult {
  readonly ok: boolean;
  readonly message: string;
}

/**
 * The Apply path: the exact structured request, sent only through the AAL2 guard to
 * set_ai_execution_profile. A refusal is reported in the owner's words; success says
 * the change was sent, never that it is running - Actual shows that once reported.
 */
export async function applyAiProfileChange(input: {
  readonly orgId: string;
  readonly view: AiExecutionProfileView;
  readonly change: { readonly offeringKey: string | null; readonly effortKey: string | null; readonly applyMode: AiProfileApplyMode };
  readonly guard: (name: 'set_ai_execution_profile', action: () => Promise<void>) => Promise<void>;
  readonly rpc: (name: 'set_ai_execution_profile', args: { readonly request: unknown }) => PromiseLike<{ data: unknown; error: unknown }>;
  readonly refresh: () => Promise<void>;
}): Promise<AiProfileApplyResult> {
  const request = aiProfileRequest({
    orgId: input.orgId, view: input.view,
    offeringKey: input.change.offeringKey, effortKey: input.change.effortKey, applyMode: input.change.applyMode,
  });
  if (request === null) return { ok: false, message: input.view.controlReason ?? aiProfileErrorMessage('') };
  let outcome: AiProfileApplyResult = { ok: false, message: aiProfileErrorMessage('') };
  await input.guard('set_ai_execution_profile', async () => {
    const { data, error } = await input.rpc('set_ai_execution_profile', { request });
    const result = data as { ok?: boolean; error?: { code?: string }; value?: { outcome?: string } } | null;
    if (error || !result?.ok) {
      outcome = { ok: false, message: aiProfileErrorMessage(result?.error?.code ?? '') };
      return;
    }
    outcome = {
      ok: true,
      message: result.value?.outcome === 'deferred'
        ? 'Saved. It applies after the current task; Actual updates when the computer reports.'
        : 'Sent to the computer. Actual updates when it reports what it is running.',
    };
    await input.refresh();
  });
  return outcome;
}
